import { useDDay } from '@hooks/useDday';
import React from 'react';
import styled from 'styled-components';

// 디자인 타입 정의
export type DdayDesignType = 'text' | 'image';

// 이미지형 옵션 타입들
export type ImageTextAlign = 'left' | 'center' | 'right';
export type ImageLayoutDirection = 'row' | 'column';

const ItemWrapper = styled.div<{
  designType: DdayDesignType;
  backgroundImage?: string;
  imageTextAlign?: ImageTextAlign;
  imageLayoutDirection?: ImageLayoutDirection;
}>`
  width: 100%;
  color: var(--textColor);
  display: flex;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;

  ${({
    designType,
    backgroundImage,
    imageTextAlign = 'right',
    imageLayoutDirection = 'row',
  }) => {
    if (designType === 'text') {
      return `
        flex-direction: column;
        justify-content: center;
        align-items: center;
      `;
    }

    // 이미지형 스타일 생성
    const flexDirection = imageLayoutDirection;
    const justifyContent =
      imageTextAlign === 'left'
        ? 'flex-start'
        : imageTextAlign === 'center'
        ? 'center'
        : 'flex-end';

    // 그라디언트 방향 계산
    const getGradientDirection = () => {
      if (imageLayoutDirection === 'column') {
        return imageTextAlign === 'left'
          ? '180deg'
          : imageTextAlign === 'center'
          ? '90deg'
          : '0deg';
      } else {
        return imageTextAlign === 'left'
          ? '90deg'
          : imageTextAlign === 'center'
          ? '0deg'
          : '-90deg';
      }
    };

    return `
      flex-direction: ${flexDirection};
      justify-content: ${justifyContent};
      align-items: ${imageLayoutDirection === 'column' ? 'stretch' : 'center'};
      padding: 16px;
      min-height: 80px;
      background: ${
        backgroundImage
          ? `
        linear-gradient(${getGradientDirection()}, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 80%),
        url(${backgroundImage})
      `
          : `
        linear-gradient(${getGradientDirection()}, var(--cardBackgroundColor) 0%, rgba(255,255,255,0.05) 100%)
      `
      };
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      border-radius: 12px;
      border: 1px solid var(--borderLineColor);
      color: white;
    `;
  }}

  img {
    object-fit: cover;
    border-radius: 6px;

    ${({ designType }) =>
      designType === 'text'
        ? `
      width: fit-content;
      height: fit-content;
      max-width: 100%;
      max-height: 78px;
    `
        : `
      display: none;
    `}
  }
`;

// 텍스트형 컨텐츠 래퍼
const TextContentsWrap = styled.div<{ primaryColor?: string }>`
  display: grid;
  width: 100%;
  padding: 5px 16px;
  transition: all 0.2s;
  gap: 8px;
  align-items: center;
  grid-template-columns: auto 1fr auto;

  .title {
    font-size: 14px;
    font-weight: 500;
  }

  .dday {
    font-size: 14px;
    color: ${({ primaryColor }) => primaryColor || 'var(--subTextColor)'};
    font-family: var(--headerFontFamily);
    font-weight: bold;
  }
`;

// 이미지형 컨텐츠 래퍼
const ImageContentsWrap = styled.div<{
  primaryColor?: string;
  imageTextAlign?: ImageTextAlign;
  imageLayoutDirection?: ImageLayoutDirection;
}>`
  display: flex;
  flex: 1;
  gap: 6px;
  position: relative;
  z-index: 2;
  color: var(--textColor);

  ${({ imageTextAlign = 'right', imageLayoutDirection = 'row' }) => {
    const alignItems =
      imageTextAlign === 'left'
        ? 'flex-start'
        : imageTextAlign === 'center'
        ? 'center'
        : 'flex-end';
    const textAlign = imageTextAlign;

    return `
      flex-direction: ${imageLayoutDirection};
      align-items: ${alignItems};
      text-align: ${textAlign};
      ${imageLayoutDirection === 'row' ? 'justify-content: space-between;' : ''}
    `;
  }}

  .title {
    font-size: 16px;
    font-weight: 700;
    color: white;
    margin: 0;
    line-height: 1.2;
  }

  .dday {
    font-size: 14px;
    background-color: ${({ primaryColor }) =>
      primaryColor || 'var(--primaryColor)'};
    width: fit-content;
    padding: 4px 8px;
    border-radius: calc(var(--cardBorderRadius) + 2px);
    color: white;
    font-family: var(--headerFontFamily);
    font-weight: bold;
    margin: 0;
  }
`;

const Dott = styled.div`
  border-top: 2px solid var(--subTextColor);
  border-style: dotted;
  height: 2px;
`;

type Props = {
  data: IddayWidget;
  designType?: DdayDesignType;
  noImage?: boolean;
  imageTextAlign?: ImageTextAlign; // 🔧 이미지형 텍스트 정렬
  imageLayoutDirection?: ImageLayoutDirection; // 🔧 이미지형 레이아웃 방향
};

function DdayItem({
  data,
  designType = 'text',
  noImage,
  imageTextAlign = 'right',
  imageLayoutDirection = 'row',
}: Props) {
  const { title, imgURL, targetDate } = data;

  const { sign, day } =
    useDDay({
      fromDate: data.targetDate,
      toDate: data.toDate,
    }) || {};

  // 텍스트형 렌더링
  const renderTextType = () => (
    <>
      {imgURL && !noImage && (
        <img src={imgURL} alt="디데이 위젯 이미지" loading="lazy" />
      )}
      <TextContentsWrap primaryColor={data.primaryColor}>
        <p className="title">{title}</p>
        <Dott />
        <b className="dday">
          {sign} {day}
        </b>
      </TextContentsWrap>
    </>
  );

  // 이미지형 렌더링
  const renderImageType = () => (
    <ImageContentsWrap
      primaryColor={data.primaryColor}
      imageTextAlign={imageTextAlign}
      imageLayoutDirection={imageLayoutDirection}
    >
      <p className="title">{title}</p>
      <p className="dday">
        {sign} {day}
      </p>
    </ImageContentsWrap>
  );

  // 디자인 타입별 매핑
  const designTypeMap = {
    text: renderTextType,
    image: renderImageType,
  };

  return (
    <ItemWrapper
      designType={designType}
      backgroundImage={
        designType === 'image' && imgURL && !noImage ? imgURL : undefined
      }
      imageTextAlign={imageTextAlign}
      imageLayoutDirection={imageLayoutDirection}
    >
      {designTypeMap[designType]()}
    </ItemWrapper>
  );
}

export default DdayItem;
